import { cn } from '@/lib/cn'
import type { ReactNode } from 'react'

type Tone = 'neutral' | 'accent' | 'positive' | 'caution' | 'critical' | 'outline'

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-ink-soft',
  accent: 'bg-accent-soft text-accent-ink',
  positive: 'bg-positive-soft text-positive',
  caution: 'bg-caution-soft text-caution',
  critical: 'bg-critical-soft text-critical',
  outline: 'border border-line-strong text-ink-soft',
}

export function Badge({
  tone = 'neutral',
  className,
  title,
  children,
}: {
  tone?: Tone
  className?: string
  title?: string
  children: ReactNode
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-micro font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Five-star rating. Readable without relying on colour. */
export function RatingStars({
  value,
  className,
  showValue = false,
}: {
  value: number | null
  className?: string
  showValue?: boolean
}) {
  if (value === null) {
    return (
      <span className={cn('text-xs text-ink-faint', className)} title="Not rated">
        —
      </span>
    )
  }
  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      title={`Rated ${value} out of 5`}
    >
      <span aria-hidden className="tracking-tight text-caution">
        {'★'.repeat(value)}
        <span className="text-line-strong">{'★'.repeat(Math.max(0, 5 - value))}</span>
      </span>
      {showValue ? (
        <span className="tabular text-xs text-ink-faint">{value}</span>
      ) : null}
      <span className="sr-only">{value} out of 5</span>
    </span>
  )
}

/**
 * Effort, shown as three bars rather than a word. The label stays in the title
 * attribute and the accessible name, so the reading never depends on the fill.
 */
export function EffortBadge({ level }: { level: number | null }) {
  if (level === null) return <span className="text-xs text-ink-faint">—</span>
  const label = level === 1 ? 'Low effort' : level === 2 ? 'Medium effort' : 'High effort'
  const fill = level === 1 ? 'bg-positive' : level === 2 ? 'bg-caution' : 'bg-critical'
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={label}
      aria-label={label}
    >
      <span aria-hidden className="flex items-end gap-px">
        {[0.32, 0.62, 1].map((height, index) => (
          <span
            key={height}
            style={{ height: `${height * 10}px` }}
            className={cn(
              'w-1 rounded-[1px]',
              index < level ? fill : 'bg-line-strong',
            )}
          />
        ))}
      </span>
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function VegetarianBadge({ vegetarian }: { vegetarian: boolean }) {
  return vegetarian ? (
    <Badge tone="positive" title="Vegetarian">
      Veg
    </Badge>
  ) : null
}
